import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';

export type AppTheme = 'light' | 'dark';

export interface PwaMode {
  isPWA: boolean;
  isInstalled: boolean;
  displayMode: string;
  isStandalone: boolean;
  isBrowser: boolean;
}

interface AppState {
  theme: string;
  navigatorOn: boolean;
  navigatorBackOff: boolean;
  openedModals: string[];
  pwaMode: PwaMode;
}

const initialState: AppState = {
  theme: localStorage.getItem('theme') ?? 'light',
  navigatorOn: true,
  navigatorBackOff: false,
  openedModals: [],
  pwaMode: {
    isPWA: false,
    isInstalled: false,
    displayMode: 'browser',
    isStandalone: false,
    isBrowser: true,
  },
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateThemeState: state => {
      state.theme = localStorage.getItem('theme') ?? 'light';
    },
    addModal: (state, action: PayloadAction<string>) => {
      state.openedModals.unshift(action.payload);
    },
    removeModal: (state, action: PayloadAction<string>) => {
      state.openedModals = state.openedModals.filter(modal => modal !== action.payload);
    },
    hideNavigator: state => {
      state.navigatorOn = false;
    },
    showNavigator: state => {
      state.navigatorOn = true;
    },
    setPwaMode: (state, action: PayloadAction<PwaMode>) => {
      state.pwaMode = action.payload;
    },
    setNavigatorBackOff: (state, action: PayloadAction<boolean>) => {
      state.navigatorBackOff = action.payload;
    },
  },
});

export const {
  updateThemeState,
  addModal,
  removeModal,
  hideNavigator,
  showNavigator,
  setPwaMode,
  setNavigatorBackOff,
} = appSlice.actions;

export const selectNavigatorState = (state: RootState) => state.app.navigatorOn;
export const selectNavigatorBackState = (state: RootState) => state.app.navigatorBackOff;
export const selectPwaModeState = (state: RootState) => state.app.pwaMode;
export const selectTheme = (state: RootState) => state.app.theme as AppTheme;

export default appSlice.reducer;
